import { useState, type ReactElement, type ReactNode } from 'react'
import { Camera, Lightbulb, Play } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { PermissionDeniedSheet } from '../components/PermissionDeniedSheet.js'
import { TryLiveButton } from '../components/TryLiveButton.js'
import type { Step } from '../state/types.js'
import { useStep, fakeLiveEnabled } from '../state/useStep.js'
import { useDemoMode, interactiveEnabled } from '../state/useDemoMode.js'
import { useRequestCameraGrant, type CameraGrant } from '../live/useRequestCameraGrant.js'

interface Props {
  title: string
  backTo: Step
  headline: string
  body: string
  tip?: string
  hero: ReactNode
  ctaLabel: string
  /**
   * Fires when the user is ready to start scanning. In fake-live mode the
   * grant is `null` (no camera invoked). In live mode it's the grant that
   * was acquired synchronously inside the CTA click — the parent owns its
   * lifetime via `useGrantLifetime`.
   */
  onProceed: (grant: CameraGrant | null) => void
  /** Invoked when the user opts out via the permission-denied sheet's "Skip". */
  onSkip: () => void
}

/**
 * The pre-scan landing card shared by all three scan screens. In live mode
 * the CTA invokes the camera permission request synchronously inside the
 * click (preserving iOS audio-gesture binding) and routes the resulting
 * grant up to the parent. In fake mode the CTA just calls onProceed(null).
 */
export const ScanInstructionsScreen = ({
  title, backTo, headline, body, tip, hero, ctaLabel, onProceed, onSkip,
}: Props): ReactElement => {
  const { goto } = useStep()
  const demo = useDemoMode()
  const { requesting, lastError, request, clearError } = useRequestCameraGrant()
  const [sheetOpen, setSheetOpen] = useState(false)

  const handleClick = async (): Promise<void> => {
    if (demo) { onProceed(null); return }
    const grant = await request()
    if (grant) onProceed(grant)
    else setSheetOpen(true)
  }

  const handleRetry = async (): Promise<void> => {
    setSheetOpen(false)
    clearError()
    const grant = await request()
    if (grant) onProceed(grant)
    else setSheetOpen(true)
  }

  const handleSheetSkip = (): void => {
    setSheetOpen(false)
    clearError()
    onSkip()
  }

  return (
    <AppShell
      title={title}
      showBack
      onBack={() => goto(backTo)}
      footer={
        <div className="flex flex-col gap-2 w-full">
          <Button size="xl" onClick={handleClick} disabled={requesting}>
            {demo ? <Play className="h-5 w-5" /> : <Camera className="h-5 w-5" />}
            {demo ? 'Watch demo' : (requesting ? 'Opening camera…' : ctaLabel)}
          </Button>
          {demo && !fakeLiveEnabled() && interactiveEnabled() && <TryLiveButton />}
        </div>
      }
    >
      <div className="mx-auto w-56 h-44 rounded-2xl bg-muted flex items-center justify-center mb-6">
        {hero}
      </div>
      <h1 className="text-center mb-3">{headline}</h1>
      <p className="text-muted-foreground text-center leading-relaxed">{body}</p>
      {tip && (
        <p className="text-muted-foreground text-center mt-4 text-sm inline-flex items-center justify-center gap-1.5 w-full">
          <Lightbulb className="h-4 w-4 text-primary" />
          <span>{tip}</span>
        </p>
      )}

      <PermissionDeniedSheet
        open={sheetOpen && lastError !== null}
        kind={lastError?.kind ?? 'unknown'}
        message={lastError?.message ?? ''}
        onRetry={() => { void handleRetry() }}
        onSkip={handleSheetSkip}
        onClose={() => setSheetOpen(false)}
      />
    </AppShell>
  )
}
